import { action, flow, observable } from 'mobx';
import querystring from 'querystring';

import { API, detectLanguageId, endpoints, formatLocale, handleError } from '../utilities';
import { LOG_TYPE_ERROR } from '../utilities/constants';

export default class AppStore {
  rootStore;

  @observable sessionId;

  @observable projectScore;

  @observable isStartingSession = false;

  @observable currentLanguage = '';

  @observable isChangedLanguage = false;

  @observable isReadyLanguage = true;

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  @action setIsChangeLanguage(value, language) {
    this.isChangedLanguage = value;
    if (value) {
      this.currentLanguage = formatLocale(language);
    }
  }

  @action setIsReadyLanguage(value) {
    this.isReadyLanguage = value;
  }

  startSession = flow(function* startSession() {
    this.isStartingSession = true;
    const languageId = detectLanguageId(this.currentLanguage);

    try {
      const response = yield API.post(
        endpoints.paths.startSession,
        querystring.stringify({
          ProjectId:
            this.rootStore.ui.project.projectId > 0
              ? this.rootStore.ui.project.projectId
              : this.rootStore.config.id,
          Source:
            this.rootStore.ui.project.source !== ''
              ? this.rootStore.ui.project.source
              : this.rootStore.config.source,
          Platform: this.rootStore.ui.isMobile ? 'Mobile' : '',
          PageTitle: document.title,
          Channel: this.rootStore.config.channel || 1,
          LangId: languageId
        })
      );

      if (response && response.string) {
        this.sessionId = response.string;
      }
    } catch (error) {
      this.log({ Message: handleError(error) });
    } finally {
      this.isStartingSession = false;
    }
  });

  updateSession = flow(function* updateSession() {
    try {
      yield API.post(
        endpoints.paths.updateSessionsHistory,
        querystring.stringify({
          ProjectId:
            this.rootStore.ui.project.projectId > 0
              ? this.rootStore.ui.project.projectId
              : this.rootStore.config.id,
          SessionId: this.sessionId
        })
      );
    } catch (error) {
      this.log({ Message: handleError(error) });
    } finally {
      /**
       * reset sessionId for StartSessionNew
       * the survey form has an old sessionId
       * all requests have a new sessionId after reopen the chat
       */
      if (this.sessionId && this.rootStore.chatWindow.isClosed) {
        this.sessionId = 0;
      }
    }
  });

  updateSessionLanguage = flow(function* updateSessionLanguage(language) {
    const languageId = detectLanguageId(language);
    try {
      yield API.post(
        endpoints.paths.updateSessionLanguage,
        querystring.stringify({
          ProjectId:
            this.rootStore.ui.project.projectId > 0
              ? this.rootStore.ui.project.projectId
              : this.rootStore.config.id,
          SessionId: this.sessionId,
          LanguageId: languageId
        })
      );
    } catch (error) {
      this.log({ Message: handleError(error) });
    }
  });

  log = flow(function* log({
    UserId = '',
    Level = LOG_TYPE_ERROR,
    Message = '',
    ErrorNumber = 'N/A',
    ErrorLine = 'N/A'
  }) {
    yield API.post(endpoints.paths.logger, {
      Module: this.rootStore.config.module,
      Source:
        this.rootStore.ui.project.source !== ''
          ? this.rootStore.ui.project.source
          : this.rootStore.config.source,
      ProjectId:
        this.rootStore.ui.project.projectId > 0
          ? this.rootStore.ui.project.projectId
          : this.rootStore.config.id,
      SessionId: this.sessionId,
      UserId,
      Level,
      Message,
      ErrorNumber,
      ErrorLine
    });
  });
}
